// pages/PrivacyPolicy.js
import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  Phone, 
  MapPin, 
  Shield, 
  Check, 
  Clock, 
  Users, 
  Database, 
  Mail, 
  Lock, 
  Globe, 
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Award,
  Heart,
  Eye,
  FileCheck
} from 'lucide-react';

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [activeSection, setActiveSection] = useState(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const sectionsRef = useRef({});

  const lastUpdated = "January 15, 2026";

  // Track scroll progress
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (scrollTop / docHeight) * 100;
      setScrollProgress(progress);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Intersection Observer for active section
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { threshold: 0.3 }
    );

    Object.values(sectionsRef.current).forEach((section) => {
      if (section) observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  const tocItems = [
    { id: 'intro', label: 'Introduction', icon: Shield },
    { id: 'info', label: 'Information We Collect', icon: Database },
    { id: 'use', label: 'How We Use Your Information', icon: Eye },
    { id: 'share', label: 'Information Sharing', icon: Globe },
    { id: 'security', label: 'Data Security', icon: Lock },
    { id: 'rights', label: 'Your Rights', icon: Award },
    { id: 'cookies', label: 'Cookies & Tracking', icon: FileCheck },
    { id: 'children', label: 'Children\'s Privacy', icon: Heart },
    { id: 'changes', label: 'Changes to Policy', icon: Clock },
    { id: 'contact', label: 'Contact Us', icon: Mail }
  ];

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <>
      {/* Scroll Progress Bar */}
      <div className="scroll-progress-bar" style={{ width: `${scrollProgress}%` }} />

      {/* Hero Section - Modern with Gradient */}
      <section className="legal-hero-modern">
        <div className="wrap">
          <div className="legal-hero-content">
            <div className="legal-hero-badge">
              <Shield size={16} />
              <span>Privacy Policy</span>
            </div>
            <h1 className="legal-hero-title">
              Your privacy <span className="highlight">matters to us.</span>
            </h1>
            <p className="legal-hero-description">
              We're committed to protecting your personal information and being transparent about how we use it.
            </p>
            <div className="legal-hero-meta">
              <div className="legal-hero-meta-item">
                <Clock size={16} />
                <span>Last updated: {lastUpdated}</span>
              </div>
              <div className="legal-hero-meta-divider" />
              <div className="legal-hero-meta-item">
                <FileCheck size={16} />
                <span>8 min read</span>
              </div>
              <div className="legal-hero-meta-divider" />
              <div className="legal-hero-meta-item">
                <Shield size={16} />
                <span>GDPR Compliant</span>
              </div>
            </div>
          </div>
        </div>
        <div className="legal-hero-particles">
          {[...Array(6)].map((_, i) => (
            <div 
              key={i} 
              className="legal-hero-particle"
              style={{
                '--delay': `${i * 0.3}s`,
                '--x': `${Math.random() * 100}%`,
                '--y': `${Math.random() * 100}%`
              }}
            />
          ))}
        </div>
      </section>

      {/* Table of Contents - Sticky Sidebar Layout */}
      <section className="legal-layout">
        <div className="wrap">
          <div className="legal-layout-grid">
            {/* Sidebar TOC */}
            <aside className="legal-sidebar">
              <div className="legal-toc-sticky">
                <div className="legal-toc-modern">
                  <div className="legal-toc-header-modern">
                    <Shield size={20} />
                    <h3>Contents</h3>
                  </div>
                  <nav className="legal-toc-nav">
                    {tocItems.map((item) => {
                      const Icon = item.icon;
                      const isActive = activeSection === item.id;
                      return (
                        <button
                          key={item.id}
                          onClick={() => scrollToSection(item.id)}
                          className={`legal-toc-nav-item ${isActive ? 'active' : ''}`}
                        >
                          <Icon size={16} />
                          <span>{item.label}</span>
                          {isActive && <div className="legal-toc-nav-indicator" />}
                        </button>
                      );
                    })}
                  </nav>
                </div>
              </div>
            </aside>

            {/* Main Content */}
            <main className="legal-main">
              {/* Introduction */}
              <div 
                id="intro" 
                className="legal-section-modern"
                ref={el => sectionsRef.current['intro'] = el}
              >
                <div className="legal-section-header-modern">
                  <div className="legal-section-number-modern">01</div>
                  <h2>Introduction</h2>
                </div>
                <div className="legal-section-body-modern">
                  <p>
                    FaithConnect ("we," "our," or "us") is committed to protecting the privacy and security of your personal information. 
                    This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform, 
                    website, and services (collectively, the "Services").
                  </p>
                  <p>
                    By using FaithConnect, you agree to the collection and use of information in accordance with this policy. 
                    We are based in Ghana and comply with applicable data protection laws, including the Data Protection Act, 2012 (Act 843).
                  </p>
                  <div className="legal-highlight-modern">
                    <div className="legal-highlight-icon">
                      <Sparkles size={20} />
                    </div>
                    <div>
                      <h4>Transparency First</h4>
                      <p>This policy explains exactly what data we collect and why. No surprises, no hidden terms.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Information We Collect */}
              <div 
                id="info" 
                className="legal-section-modern"
                ref={el => sectionsRef.current['info'] = el}
              >
                <div className="legal-section-header-modern">
                  <div className="legal-section-number-modern">02</div>
                  <h2>Information We Collect</h2>
                </div>
                <div className="legal-section-body-modern">
                  <p>We collect information that you provide directly to us, as well as information automatically collected through your use of our Services.</p>
                  
                  <div className="legal-info-grid">
                    <div className="legal-info-card">
                      <div className="legal-info-card-icon">👤</div>
                      <h4>Information You Provide</h4>
                      <ul className="legal-list-modern">
                        <li><strong>Account Information:</strong> Name, email, phone, church name, role</li>
                        <li><strong>Church Member Data:</strong> Names, phone numbers, attendance records</li>
                        <li><strong>Communication Data:</strong> Messages, SMS content, preferences</li>
                        <li><strong>Payment Information:</strong> Billing details (processed securely)</li>
                        <li><strong>Support Data:</strong> Information from support inquiries</li>
                      </ul>
                    </div>
                    
                    <div className="legal-info-card">
                      <div className="legal-info-card-icon">🤖</div>
                      <h4>Automatically Collected</h4>
                      <ul className="legal-list-modern">
                        <li><strong>Usage Data:</strong> How you interact with our Services</li>
                        <li><strong>Device Information:</strong> Device type, OS, browser, IP address</li>
                        <li><strong>Location Data:</strong> Approximate location (with permission)</li>
                        <li><strong>Cookies:</strong> Small files to enhance your experience</li>
                      </ul>
                    </div>
                  </div>

                  <div className="legal-data-grid-modern">
                    <div className="legal-data-item-modern">
                      <div className="legal-data-icon"><Database size={24} /></div>
                      <div>
                        <h5>You Control Your Data</h5>
                        <p>All church member data belongs to you. We process it only as instructed.</p>
                      </div>
                    </div>
                    <div className="legal-data-item-modern">
                      <div className="legal-data-icon"><Lock size={24} /></div>
                      <div>
                        <h5>Enterprise-Grade Security</h5>
                        <p>All sensitive data is encrypted at rest (AES-256) and in transit (TLS 1.3).</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* How We Use Information */}
              <div 
                id="use" 
                className="legal-section-modern"
                ref={el => sectionsRef.current['use'] = el}
              >
                <div className="legal-section-header-modern">
                  <div className="legal-section-number-modern">03</div>
                  <h2>How We Use Your Information</h2>
                </div>
                <div className="legal-section-body-modern">
                  <p>We use your information to provide, improve, and protect our Services:</p>
                  
                  <div className="legal-use-grid">
                    {[
                      { icon: '📊', title: 'Provide Services', desc: 'Deliver attendance tracking, SMS notifications, member management, and other core features.' },
                      { icon: '📱', title: 'Communication', desc: 'Send service updates, support messages, and important notifications.' },
                      { icon: '📈', title: 'Improvement', desc: 'Analyze usage patterns to enhance features and user experience.' },
                      { icon: '🔒', title: 'Security', desc: 'Detect and prevent fraud, abuse, and unauthorized access.' },
                      { icon: '⚖️', title: 'Legal Compliance', desc: 'Fulfill legal obligations and respond to lawful requests.' }
                    ].map((item, idx) => (
                      <div key={idx} className="legal-use-item">
                        <div className="legal-use-icon">{item.icon}</div>
                        <div>
                          <h5>{item.title}</h5>
                          <p>{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="legal-highlight-modern gold">
                    <div className="legal-highlight-icon">
                      <Check size={20} />
                    </div>
                    <div>
                      <h4>We Never Sell Your Data</h4>
                      <p>Your trust is our most valuable asset. We will never sell your personal information to third parties.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Information Sharing */}
              <div 
                id="share" 
                className="legal-section-modern"
                ref={el => sectionsRef.current['share'] = el}
              >
                <div className="legal-section-header-modern">
                  <div className="legal-section-number-modern">04</div>
                  <h2>Information Sharing</h2>
                </div>
                <div className="legal-section-body-modern">
                  <p>We share information only in these limited circumstances:</p>
                  
                  <div className="legal-share-grid">
                    {[
                      { icon: '🤝', title: 'With Your Consent', desc: 'When you direct us to share information with third parties.' },
                      { icon: '🏢', title: 'Service Providers', desc: 'Trusted vendors who help us deliver our Services (e.g., SMS providers, hosting).' },
                      { icon: '⚖️', title: 'Legal Requirements', desc: 'When required by law, court order, or government regulation.' },
                      { icon: '🏗️', title: 'Business Transfers', desc: 'In connection with a merger, acquisition, or sale of assets.' }
                    ].map((item, idx) => (
                      <div key={idx} className="legal-share-item">
                        <div className="legal-share-icon">{item.icon}</div>
                        <div>
                          <h5>{item.title}</h5>
                          <p>{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="legal-note-modern">
                    <div className="legal-note-icon">📱</div>
                    <div>
                      <strong>SMS Partners:</strong> SMS messages are sent through our trusted telecommunications partners in Ghana. 
                      These partners are bound by strict confidentiality agreements.
                    </div>
                  </div>
                </div>
              </div>

              {/* Data Security */}
              <div 
                id="security" 
                className="legal-section-modern"
                ref={el => sectionsRef.current['security'] = el}
              >
                <div className="legal-section-header-modern">
                  <div className="legal-section-number-modern">05</div>
                  <h2>Data Security</h2>
                </div>
                <div className="legal-section-body-modern">
                  <p>We implement industry-standard security measures to protect your data:</p>
                  
                  <div className="legal-security-grid-modern">
                    {[
                      { icon: Lock, title: 'Encryption', desc: 'All data encrypted at rest (AES-256) and in transit (TLS 1.3)', color: '#6C2BD1' },
                      { icon: Shield, title: 'Access Control', desc: 'Strict role-based access to all systems and data', color: '#2963E1' },
                      { icon: Clock, title: 'Regular Audits', desc: 'Security audits and penetration testing performed regularly', color: '#307A4E' },
                      { icon: Users, title: 'Staff Training', desc: 'All staff trained in data protection and security best practices', color: '#D4770F' }
                    ].map((item, idx) => {
                      const Icon = item.icon;
                      return (
                        <div key={idx} className="legal-security-item-modern">
                          <div className="legal-security-icon" style={{ background: `${item.color}20`, color: item.color }}>
                            <Icon size={24} />
                          </div>
                          <h5>{item.title}</h5>
                          <p>{item.desc}</p>
                        </div>
                      );
                    })}
                  </div>

                  <div className="legal-highlight-modern blue">
                    <div className="legal-highlight-icon">
                      <Heart size={20} />
                    </div>
                    <div>
                      <h4>Treating Your Data as Our Own</h4>
                      <p>We treat your church members' data with the same care and respect we would our own personal information.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Your Rights */}
              <div 
                id="rights" 
                className="legal-section-modern"
                ref={el => sectionsRef.current['rights'] = el}
              >
                <div className="legal-section-header-modern">
                  <div className="legal-section-number-modern">06</div>
                  <h2>Your Rights</h2>
                </div>
                <div className="legal-section-body-modern">
                  <p>You have the following rights regarding your personal data:</p>
                  
                  <div className="legal-rights-grid">
                    {[
                      { icon: '🔍', title: 'Access', desc: 'Request a copy of the data we hold about you.' },
                      { icon: '✏️', title: 'Rectification', desc: 'Correct inaccurate or incomplete data.' },
                      { icon: '🗑️', title: 'Deletion', desc: 'Request deletion of your data (subject to legal requirements).' },
                      { icon: '⏸️', title: 'Restriction', desc: 'Restrict processing of your data in certain circumstances.' },
                      { icon: '📤', title: 'Portability', desc: 'Request a copy of your data in a machine-readable format.' },
                      { icon: '❌', title: 'Objection', desc: 'Object to data processing for specific purposes.' }
                    ].map((item, idx) => (
                      <div key={idx} className="legal-rights-item">
                        <div className="legal-rights-icon">{item.icon}</div>
                        <div>
                          <h5>{item.title}</h5>
                          <p>{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="legal-contact-prompt">
                    <Mail size={20} />
                    <div>
                      <p>To exercise any of these rights, contact us at:</p>
                      <a href="mailto:privacy@faithconnect.com" className="legal-email-link">privacy@faithconnect.com</a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Cookies */}
              <div 
                id="cookies" 
                className="legal-section-modern"
                ref={el => sectionsRef.current['cookies'] = el}
              >
                <div className="legal-section-header-modern">
                  <div className="legal-section-number-modern">07</div>
                  <h2>Cookies & Tracking</h2>
                </div>
                <div className="legal-section-body-modern">
                  <p>We use cookies and similar tracking technologies to:</p>
                  
                  <div className="legal-cookies-grid">
                    {[
                      { icon: '🔧', title: 'Essential', desc: 'Necessary for basic functionality of our Services.' },
                      { icon: '🎯', title: 'Preference', desc: 'Remember your settings and preferences.' },
                      { icon: '📊', title: 'Analytics', desc: 'Understand how you use our Services and improve them.' },
                      { icon: '📣', title: 'Marketing', desc: 'Deliver relevant advertisements (only with your consent).' }
                    ].map((item, idx) => (
                      <div key={idx} className="legal-cookies-item">
                        <div className="legal-cookies-icon">{item.icon}</div>
                        <div>
                          <h5>{item.title}</h5>
                          <p>{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <p className="legal-cookies-note">
                    You can control cookie preferences in your browser settings. However, disabling essential cookies may affect functionality.
                  </p>
                </div>
              </div>

              {/* Children's Privacy */}
              <div 
                id="children" 
                className="legal-section-modern"
                ref={el => sectionsRef.current['children'] = el}
              >
                <div className="legal-section-header-modern">
                  <div className="legal-section-number-modern">08</div>
                  <h2>Children's Privacy</h2>
                </div>
                <div className="legal-section-body-modern">
                  <div className="legal-children-content">
                    <div className="legal-children-icon">👶</div>
                    <div>
                      <p>
                        Our Services are not directed at children under the age of 13. We do not knowingly collect personal information from children under 13. If you believe we have collected such information, please contact us immediately.
                      </p>
                      <div className="legal-note-modern">
                        <div className="legal-note-icon">ℹ️</div>
                        <div>
                          <strong>For Church Administrators:</strong> You may include information about minors in your member records. You are responsible for obtaining appropriate consent from parents or guardians.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Changes to Policy */}
              <div 
                id="changes" 
                className="legal-section-modern"
                ref={el => sectionsRef.current['changes'] = el}
              >
                <div className="legal-section-header-modern">
                  <div className="legal-section-number-modern">09</div>
                  <h2>Changes to Policy</h2>
                </div>
                <div className="legal-section-body-modern">
                  <p>We may update this Privacy Policy from time to time. We will notify you of any changes by:</p>
                  
                  <ul className="legal-list-modern">
                    <li>Posting the new policy on this page.</li>
                    <li>Updating the "Last Updated" date at the top of this page.</li>
                    <li>Sending you a notification (for significant changes).</li>
                  </ul>
                  
                  <div className="legal-highlight-modern subtle">
                    <div className="legal-highlight-icon">
                      <Clock size={20} />
                    </div>
                    <div>
                      <h4>Stay Informed</h4>
                      <p>We encourage you to review this policy periodically to stay informed about how we protect your information.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact */}
              <div 
                id="contact" 
                className="legal-section-modern"
                ref={el => sectionsRef.current['contact'] = el}
              >
                <div className="legal-section-header-modern">
                  <div className="legal-section-number-modern">10</div>
                  <h2>Contact Us</h2>
                </div>
                <div className="legal-section-body-modern">
                  <p>If you have questions, concerns, or requests regarding this Privacy Policy:</p>
                  
                  <div className="legal-contact-grid-modern">
                    <a href="mailto:privacy@faithconnect.com" className="legal-contact-item-modern">
                      <div className="legal-contact-icon">
                        <Mail size={20} />
                      </div>
                      <div className="legal-contact-text">
                        <span className="legal-contact-label">Email</span>
                        <span className="legal-contact-value">privacy@faithconnect.com</span>
                      </div>
                      <ArrowRight size={16} className="legal-contact-arrow" />
                    </a>
                    
                    <a href="tel:+233200000000" className="legal-contact-item-modern">
                      <div className="legal-contact-icon">
                        <Phone size={20} />
                      </div>
                      <div className="legal-contact-text">
                        <span className="legal-contact-label">Phone</span>
                        <span className="legal-contact-value">+233 20 000 0000</span>
                      </div>
                      <ArrowRight size={16} className="legal-contact-arrow" />
                    </a>
                    
                    <div className="legal-contact-item-modern">
                      <div className="legal-contact-icon">
                        <MapPin size={20} />
                      </div>
                      <div className="legal-contact-text">
                        <span className="legal-contact-label">Location</span>
                        <span className="legal-contact-value">Accra, Ghana</span>
                      </div>
                    </div>
                  </div>

                  <div className="legal-response-time">
                    <div className="legal-response-time-icon">⏰</div>
                    <div>
                      <h4>Quick Response Guarantee</h4>
                      <p>We respond to all inquiries within 48 hours. Your privacy concerns are our priority.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Back to Top */}
              <div className="legal-back-to-top">
                <button onClick={() => scrollToSection('intro')} className="legal-back-to-top-btn">
                  <ChevronUp size={20} />
                  Back to top
                </button>
              </div>
            </main>
          </div>
        </div>
      </section>
    </>
  );
};

export default PrivacyPolicy;